import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ActionDropdown } from '@/components';
import { useShipmentLabels } from '@/components/LabelPreviewModal/useShipmentLabels';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';
import { useUrlPatch } from '@/hooks/urlState';

import {
  errorMessage,
  isAbortError,
  warehouseApi,
} from '../../api/warehouse.api';
import {
  BulkBar,
  EmptyTable,
  ListSearch,
  ListShell,
} from '../../components/ListShell';
import OutboundCsvImportModal from '../../components/OutboundCsvImportModal';
import { OutboundTransferTag } from '../../components/orderType';
import OutboundRequestFormModal from '../../components/OutboundRequestFormModal';
import { HeaderActions } from '../../components/pickers';
import {
  codeCls,
  Dash,
  dateCls,
  LinesQty,
  LoadError,
  MappedBadge,
  metaCls,
  nameCls,
  PageHeader,
  SourceTag,
} from '../../components/ui';
import { useOutboundActions } from '../../components/useOutboundActions';
import { useOutboundShippingLabels } from '../../components/useOutboundShippingLabels';
import { downloadBlob, fmtDate, fmtDateTime, fmtQty } from '../../format';
import { useCappedSelection } from '../../hooks/useCappedSelection';
import { WarehouseFilterPanel } from '../../components/WarehouseFilterPanel';
import { OUTBOUND_FILTERS } from '../../filterFields';
import { filterParamKeys } from '../../hooks/useListFilters';
import { useListQuery } from '../../hooks/useListQuery';
import { useLoad } from '../../hooks/useLoad';
import { shipmentsUrl } from '../../links';
import { hasShippingLabel } from '../../print/shippingLabels';
import {
  isPickingStage,
  methodLabel,
  OUTBOUND_CANCELLABLE,
  OUTBOUND_STATUS,
  OUTBOUND_TABS,
} from '../../status';
import type { OutboundOrder, OutboundOrderStatus } from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { WAREHOUSE_PATHS } from '../../paths';

type Tab = (typeof OUTBOUND_TABS)[number];

const OutboundRequestListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hubID, ready, refreshToken, refresh } = useWarehouse();
  const q = useListQuery<Tab>(OUTBOUND_TABS, 'ALL', OUTBOUND_FILTERS);
  const { api: filterApi, apiKey: filterKey } = q.filters;
  const search = q.search.committed || null;
  const { patch } = useUrlPatch();
  const selection = useCappedSelection();
  const { selected, setSelected, clear: clearSelection } = selection;
  const [modal, setModal] = useState<'create' | 'csv' | null>(null);

  const list = useLoad(
    (signal) =>
      warehouseApi.listOutboundOrders(
        {
          hubID,
          status: q.tab === 'ALL' ? null : (q.tab as OutboundOrderStatus),
          ...filterApi,
          search,
          page: q.page,
          limit: q.limit,
        },
        { signal }
      ),
    [hubID, q.tab, filterKey, search, q.page, q.limit, refreshToken],
    ready
  );
  // Tab counts: the list's filters (minus the tab) — never disagree with it.
  const summary = useLoad(
    (signal) =>
      warehouseApi.getOutboundSummary(
        { hubID, ...filterApi, search },
        { signal }
      ),
    [hubID, filterKey, search, refreshToken],
    ready
  );

  // A mutation already refreshed the badges; reload what this page shows.
  const reloadList = list.reload;
  const reloadSummary = summary.reload;
  const onChanged = useCallback(() => {
    reloadList();
    reloadSummary();
  }, [reloadList, reloadSummary]);
  const actions = useOutboundActions(onChanged);

  // CSV of what the page shows (tab + filters + search), every page — the
  // same "Download CSV" the Link client portal has on this list.
  const [exporting, setExporting] = useState(false);
  const exportAbort = useRef<AbortController | null>(null);
  useEffect(() => () => exportAbort.current?.abort(), []);
  const downloadCsv = async () => {
    exportAbort.current?.abort();
    const controller = new AbortController();
    exportAbort.current = controller;
    setExporting(true);
    try {
      const file = await warehouseApi.downloadOutboundOrdersCsv(
        {
          hubID,
          status: q.tab === 'ALL' ? null : (q.tab as OutboundOrderStatus),
          ...filterApi,
          search,
        },
        { signal: controller.signal }
      );
      downloadBlob(file.blob, file.filename);
      toast.success('CSV outbound request berhasil di-download.');
    } catch (err) {
      if (!isAbortError(err)) {
        toast.error('CSV outbound request gagal di-download', {
          description: errorMessage(err),
        });
      }
    } finally {
      if (exportAbort.current === controller) setExporting(false);
    }
  };
  // Bulk shipping labels: Dash orders from their shipment, 3PL orders from the order.
  const labels = useShipmentLabels();
  const outboundLabels = useOutboundShippingLabels(labels.open);
  const { requestPicking, requestCancel, isBusy } = actions;

  useEffect(() => {
    clearSelection();
  }, [q.tab, q.page, filterKey, hubID, refreshToken, clearSelection]);

  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    summary.data?.byStatus.forEach((b) => m.set(b.status, b.count));
    return m;
  }, [summary.data]);

  const tabs = OUTBOUND_TABS.map((k) => ({
    key: k,
    label: k === 'ALL' ? 'All' : OUTBOUND_STATUS[k].label,
    count: summary.data
      ? k === 'ALL'
        ? summary.data.total
        : (counts.get(k) ?? 0)
      : null,
  }));

  const selectedOrders = rows.filter(
    (o) => selected.includes(o.id) && o.status === 'OPEN'
  );
  // Orders with an AWB (shipped on) print their labels; Open ones keep their own bar.
  const printableOrders = rows.filter(
    (o) => selected.includes(o.id) && hasShippingLabel(o.fulfillment)
  );

  const columns = useMemo<TableDataColumn<OutboundOrder>[]>(
    () => [
      {
        key: 'ref',
        title: 'Request ID / External ID',
        render: (_v, o) => (
          <div>
            <p className={codeCls}>{o.code}</p>
            <p className={`${metaCls} font-mono`}>{o.externalID}</p>
          </div>
        ),
      },
      {
        key: 'source',
        title: 'Source',
        render: (_v, o) => (
          <div className="flex flex-col items-start gap-1">
            <SourceTag source={o.source} />
            {o.upload && (
              <span className="font-mono text-[10px] text-text-soft-400">
                {o.upload.code}
              </span>
            )}
            <OutboundTransferTag order={o} />
          </div>
        ),
      },
      {
        key: 'client',
        title: 'Client',
        render: (_v, o) => (
          <span className={nameCls}>
            {o.client?.name ?? `Client #${o.clientID}`}
          </span>
        ),
      },
      {
        key: 'recipient',
        title: 'Recipient',
        render: (_v, o) => (
          <div>
            <p className={nameCls}>{o.recipientName}</p>
            <p className={`${metaCls} max-w-[240px] truncate`}>
              {o.destinationCity ? (
                <span className="text-text-sub-600">{o.destinationCity}</span>
              ) : (
                <span
                  className="text-warning-base"
                  title="Kota belum dikenali — pilih lewat Ubah"
                >
                  <i className="ri-error-warning-line" /> Kota belum dikenali
                </span>
              )}
              {' · '}
              {o.destinationAddress}
            </p>
          </div>
        ),
      },
      {
        key: 'lines',
        title: 'Lines / qty',
        align: 'right',
        render: (_v, o) => (
          <LinesQty lines={o.lineCount} qty={fmtQty(o.totalQty)} />
        ),
      },
      {
        key: 'method',
        title: 'Method',
        render: (_v, o) => (
          <span className={metaCls}>{methodLabel(o.method)}</span>
        ),
      },
      {
        key: 'expected',
        title: 'Expected',
        render: (_v, o) => (
          <span className={dateCls}>{fmtDate(o.expectedDeliveryDate)}</span>
        ),
      },
      {
        key: 'hub',
        title: 'Hub',
        render: (_v, o) => (
          <span className={nameCls}>{o.hub?.name ?? `Hub #${o.hubID}`}</span>
        ),
      },
      {
        key: 'orderStatus',
        title: 'Status',
        render: (_v, o) => (
          <MappedBadge map={OUTBOUND_STATUS} value={o.status} />
        ),
      },
      {
        key: 'shipment',
        title: 'Shipment',
        render: (_v, o) =>
          o.fulfillment?.waybill ? (
            <Link
              to={shipmentsUrl(o.fulfillment)}
              onClick={(e) => e.stopPropagation()}
              className={`${codeCls} text-accent hover:underline underline-offset-4`}
            >
              {o.fulfillment.waybill}
            </Link>
          ) : (
            <Dash />
          ),
      },
      {
        key: 'created',
        title: 'Created',
        render: (_v, o) => (
          <span className={dateCls}>{fmtDateTime(o.createdAt)}</span>
        ),
      },
      {
        key: 'actions',
        title: '',
        align: 'right',
        render: (_v, o) => (
          <ActionDropdown
            busy={isBusy(o.id)}
            actions={[
              {
                key: 'view',
                label: 'View details',
                icon: 'ri-eye-line',
                onClick: () =>
                  navigate(`${WAREHOUSE_PATHS.outboundRequest}/${o.id}`),
              },
              {
                key: 'picking',
                label: 'Create picking order',
                icon: 'ri-file-list-3-line',
                hidden: o.status !== 'OPEN',
                disabled: isBusy(o.id),
                onClick: () => requestPicking(o),
              },
              {
                key: 'open-picking',
                label: 'Buka picking',
                icon: 'ri-box-3-line',
                hidden: !o.goodsIssue || !isPickingStage(o.goodsIssue.status),
                onClick: () =>
                  o.goodsIssue
                    ? navigate(`${WAREHOUSE_PATHS.picking}/${o.goodsIssue.id}`)
                    : undefined,
              },
              {
                key: 'open-shipping',
                label: 'Buka shipping',
                icon: 'ri-truck-line',
                hidden:
                  !o.goodsIssue ||
                  !['READY_TO_PICKUP', 'SHIPPED'].includes(o.goodsIssue.status),
                onClick: () =>
                  o.goodsIssue
                    ? navigate(`${WAREHOUSE_PATHS.shipping}/${o.goodsIssue.id}`)
                    : undefined,
              },
              {
                key: 'cancel',
                label: 'Cancel order',
                icon: 'ri-close-circle-line',
                destructive: true,
                hidden: !OUTBOUND_CANCELLABLE.includes(o.status),
                onClick: () => requestCancel(o),
              },
            ]}
          />
        ),
      },
    ],
    [navigate, requestPicking, requestCancel, isBusy]
  );

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        eyebrow="Warehouse · Outbound management"
        title="Outbound request"
        actions={
          <HeaderActions>
            <Button
              tone="neutral"
              style="stroke"
              loading={exporting}
              disabled={!ready}
              data-testid="outbound-csv-download"
              onClick={() => void downloadCsv()}
              leftIcon={<i className="ri-download-2-line text-[14px]" />}
            >
              Download CSV
            </Button>
            <Button
              tone="neutral"
              style="stroke"
              onClick={() => setModal('csv')}
              leftIcon={<i className="ri-upload-2-line text-[14px]" />}
            >
              Upload CSV
            </Button>
            <Button
              onClick={() => setModal('create')}
              leftIcon={<i className="ri-add-line text-[14px]" />}
            >
              Create outbound request
            </Button>
          </HeaderActions>
        }
      />

      <ListShell
        tabs={tabs}
        activeTab={q.tab}
        onTabChange={q.setTab}
        filters={
          <>
            <ListSearch
              value={q.search.value}
              onChange={q.search.setValue}
              placeholder="Request ID, external ID, upload, waybill…"
            />
            <WarehouseFilterPanel
              filters={q.filters}
              title="Filter outbound request"
            />
          </>
        }
        bulkBar={
          <>
            <BulkBar
              count={selectedOrders.length}
              noun="order Open dipilih"
              actionLabel={`Create picking order (${selectedOrders.length})`}
              actionIcon="ri-file-list-3-line"
              busy={actions.bulkBusy}
              capped={selection.capped}
              onClear={clearSelection}
              onAction={() => actions.requestBulkPicking(selectedOrders)}
              secondary={{
                label: `Cancel order (${selectedOrders.length})`,
                icon: 'ri-close-circle-line',
                onAction: () => actions.requestBulkCancel(selectedOrders),
              }}
            />
            <BulkBar
              count={printableOrders.length}
              noun="order dengan waybill dipilih"
              actionLabel={`Print shipping labels (${printableOrders.length})`}
              actionIcon="ri-printer-line"
              busy={labels.preparing || outboundLabels.preparing}
              onClear={clearSelection}
              onAction={() => outboundLabels.printOrders(printableOrders)}
            />
          </>
        }
        pagination={{
          page: q.page,
          limit: q.limit,
          total: list.data?.pagination.total ?? 0,
          onPageChange: q.setPage,
          onLimitChange: q.setLimit,
        }}
      >
        {list.error ? (
          <LoadError message={list.error.message} onRetry={list.reload} />
        ) : (
          <TableData
            size="sm"
            loading={list.loading || !ready}
            data={rows}
            columns={columns}
            selection={{
              selectedKeys: selected,
              onSelectionChange: setSelected,
              getRowKey: (o) => o.id,
              isRowSelectable: (o) =>
                o.status === 'OPEN' || hasShippingLabel(o.fulfillment),
            }}
            onRowClick={(o) =>
              navigate(`${WAREHOUSE_PATHS.outboundRequest}/${o.id}`)
            }
            emptyState={<EmptyTable title="Belum ada outbound request" />}
          />
        )}
      </ListShell>

      {actions.dialogs}
      {labels.modal}

      <OutboundRequestFormModal
        open={modal === 'create'}
        initialHubID={hubID}
        onClose={() => setModal(null)}
        onCreated={(res) => {
          setModal(null);
          if (res.result === 'EXISTS') {
            toast.info('Ref client sudah ada', {
              description: res.order.code,
            });
          } else {
            toast.success('Outbound request dibuat', {
              description: res.order.code,
            });
          }
          if (res.stockWarnings?.length) {
            toast.warning('Stok belum cukup', {
              description: res.stockWarnings
                .map(
                  (w) =>
                    `${w.sku}: butuh ${fmtQty(w.requested)}, tersedia ${fmtQty(w.available)}`
                )
                .join(' · '),
            });
          }
          navigate(`${WAREHOUSE_PATHS.outboundRequest}/${res.order.id}`);
        }}
      />
      <OutboundCsvImportModal
        open={modal === 'csv'}
        initialHubID={hubID}
        onClose={() => setModal(null)}
        onImported={(res) => {
          setModal(null);
          toast.success(`${res.upload.createdCount} outbound request dibuat`, {
            description: `${res.upload.code} · ${res.upload.fileName}${
              res.upload.skippedCount
                ? ` · ${res.upload.skippedCount} dilewati (sudah ada)`
                : ''
            }`,
          });
          // One URL write: all tabs, no other filter, just this batch.
          patch((params) => {
            params.delete('tab');
            params.delete('q');
            params.delete('page');
            filterParamKeys(OUTBOUND_FILTERS).forEach((k) => params.delete(k));
            params.set('uploadID', res.upload.id);
            params.set('uploadCode', res.upload.code);
          }, 'push');
          refresh();
        }}
      />
    </div>
  );
};

export default OutboundRequestListPage;
